import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Eye, RefreshCw, Download, Trash2 } from 'lucide-react'
import type { User } from '@/types/api'
import { useAuth } from '@/hooks/use-auth'

const STORAGE_KEY = 'mock_users_data'

export function MockStorageViewer() {
  const { user } = useAuth()
  const [users, setUsers] = useState<User[]>([])
  const [isVisible, setIsVisible] = useState(false)

  // 開發工具：正式 build 一律隱藏；開發環境限 admin
  if (import.meta.env.PROD) {
    return null
  }
  // 只有 admin 角色才能看到此組件
  if (!user || user.role !== 'admin') {
    return null
  }

  const loadUsers = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const parsedUsers = JSON.parse(stored)
        setUsers(parsedUsers)
        console.log('MockStorageViewer: Loaded users from localStorage:', parsedUsers)
      } else {
        setUsers([])
        console.log('MockStorageViewer: No users found in localStorage')
      }
    } catch (error) {
      console.error('MockStorageViewer: Error loading users from localStorage:', error)
      setUsers([])
    }
  }

  const downloadUsers = () => {
    const dataStr = JSON.stringify(users, null, 2)
    const dataBlob = new Blob([dataStr], { type: 'application/json' })
    const url = URL.createObjectURL(dataBlob)
    const link = document.createElement('a')
    link.href = url
    link.download = `mock-users-${new Date().toISOString().split('T')[0]}.json`
    link.click()
    URL.revokeObjectURL(url)
  }

  const clearUsers = () => {
    if (confirm('Are you sure you want to clear all users from localStorage?')) {
      localStorage.removeItem(STORAGE_KEY)
      setUsers([])
      console.log('MockStorageViewer: Cleared users from localStorage')
    }
  }

  const resetToOriginal = () => {
    if (confirm('Are you sure you want to reset to original JSON data?')) {
      // This will trigger re-initialization on next page load
      localStorage.removeItem(STORAGE_KEY)
      localStorage.removeItem('mock_courses_data')
      localStorage.removeItem('mock_enrollments_data')
      localStorage.removeItem('mock_combos_data')
      localStorage.removeItem('mock_config_data')
      console.log('MockStorageViewer: Reset all mock data')
      window.location.reload()
    }
  }

  useEffect(() => {
    if (isVisible) {
      loadUsers()
    }
  }, [isVisible])

  const telegramUsers = users.filter(user => user.telegramId)
  const recentUsers = users
    .filter(user => user.telegramId)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 5)

  return (
    <div className="fixed bottom-4 right-20 z-50">
      {!isVisible ? (
        <Button
          onClick={() => setIsVisible(true)}
          className="rounded-full h-12 w-12"
          variant="outline"
          title="查看 Mock Storage 數據"
        >
          <Eye className="h-5 w-5" />
        </Button>
      ) : (
        <Card className="w-96 max-h-96 overflow-auto">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center justify-between">
              Mock Storage 用戶數據
              <Button
                onClick={() => setIsVisible(false)}
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
              >
                ×
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>總用戶: {users.length}</span>
              <span>Telegram 用戶: {telegramUsers.length}</span>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={loadUsers}
                size="sm"
                variant="outline"
                className="flex-1"
              >
                <RefreshCw className="h-3 w-3 mr-1" />
                重新載入
              </Button>
              <Button
                onClick={downloadUsers}
                size="sm"
                variant="outline"
                className="flex-1"
              >
                <Download className="h-3 w-3 mr-1" />
                下載
              </Button>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={clearUsers}
                size="sm"
                variant="destructive"
                className="flex-1"
              >
                <Trash2 className="h-3 w-3 mr-1" />
                清空
              </Button>
              <Button
                onClick={resetToOriginal}
                size="sm"
                variant="secondary"
                className="flex-1"
              >
                重置
              </Button>
            </div>

            {recentUsers.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-xs font-medium text-muted-foreground">最近創建的 Telegram 用戶:</h4>
                {recentUsers.map(user => (
                  <div key={user.id} className="p-2 bg-secondary rounded text-xs">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">
                        {user.firstName} {user.lastName}
                      </span>
                      <Badge variant={user.role === 'student' ? 'secondary' : 'default'} className="text-xs">
                        {user.role}
                      </Badge>
                    </div>
                    <div className="text-muted-foreground mt-1">
                      <div>TG ID: {user.telegramId}</div>
                      <div>創建: {new Date(user.createdAt).toLocaleString('zh-TW')}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="text-xs text-muted-foreground pt-2 border-t">
              數據存儲在 localStorage 中，鍵名: {STORAGE_KEY}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}